import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

import MostLovedProductCard from '../landing/MostLovedProductCard';
import { v2Colors } from '../../design/theme';
import { getProducts } from '../../services/commerce';

const DISPLAY_FONT = '"Foregen", "Roboto Slab", Georgia, serif';
const FETCH_LIMIT = 12;
const DISPLAY_LIMIT = 10;

export default function CategorySuggestions({ category, excludeProductId, storeId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const name = String(category || '').trim();
    if (!name) {
      setProducts([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await getProducts({
          category: name,
          storeId: storeId || undefined,
          limit: FETCH_LIMIT,
        });
        if (cancelled) return;
        const exclude = String(excludeProductId ?? '');
        const list = (Array.isArray(res?.data) ? res.data : [])
          .filter((p) => String(p?.id ?? '') !== exclude)
          .slice(0, DISPLAY_LIMIT);
        setProducts(list);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [category, excludeProductId, storeId]);

  if (!String(category || '').trim()) return null;
  if (!loading && products.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        component="h2"
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: { xs: '1.1rem', md: '1.25rem' },
          color: v2Colors.heading,
          letterSpacing: '-0.02em',
          mb: 1.5,
        }}
      >
        More from this category
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', gap: 1.5, overflow: 'hidden' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={168}
              height={210}
              sx={{ flexShrink: 0, borderRadius: 2.5 }}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 0.5,
            mx: { xs: -1.5, sm: -2, md: 0 },
            px: { xs: 1.5, sm: 2, md: 0 },
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {products.map((p) => (
            <Box key={p.id || p.documentId || p.name} sx={{ scrollSnapAlign: 'start' }}>
              <MostLovedProductCard product={p} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
